<!-- eslint-disable vue/no-template-shadow -->
<template>
  <div class="ws-test">
    <APageHead title="웹소켓 테스트" sub="실시간 미리보기 데이터 송수신을 점검합니다.">
      <template #actions>
        <APill :kind="isConnected ? 'ok' : 'default'" dot>
          {{ isConnected ? 'CONNECTED' : 'DISCONNECTED' }}
        </APill>
      </template>
    </APageHead>

    <ACard title="연결 설정">
      <div class="ws-test__grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">badge</i>
            Client ID
          </label>
          <input v-model="clientId" class="p-input" placeholder="예: section_test@example.com" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">link</i>
            WebSocket 주소
          </label>
          <input v-model="websocketUrl" class="p-input" readonly />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">qr_code</i>
            바코드
          </label>
          <input v-model="barcode" class="p-input" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">menu_book</i>
            챕터
          </label>
          <input v-model.number="chapterNumber" type="number" min="1" class="p-input" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">format_list_numbered</i>
            섹션
          </label>
          <input v-model.number="uniqueKey" type="number" min="1" class="p-input" />
        </div>
      </div>

      <div class="p-field ws-test__preview">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">code</i>
          전송 메시지 (Preview JSON)
        </label>
        <textarea v-model="previewJson" class="p-input p-input--area" rows="5"></textarea>
      </div>

      <div class="ws-test__actions">
        <ABtn kind="yellow" icon="link" :disabled="isConnected" @click="connectWebSocket">
          웹소켓 연결
        </ABtn>
        <ABtn kind="primary" icon="send" :disabled="!isConnected" @click="sendPreview">
          미리보기 전송
        </ABtn>
        <ABtn icon="link_off" :disabled="!isConnected" @click="disconnectWebSocket">
          연결 종료
        </ABtn>
        <ABtn kind="danger" icon="clear" @click="clearLogs">로그 초기화</ABtn>
      </div>
    </ACard>

    <ACard>
      <template #title>
        <span class="ws-test__log-title">
          <i class="material-icons-round ws-test__log-icon">terminal</i>
          로그
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ logs.length }} 건</APill>
      </template>

      <AEmpty v-if="logs.length === 0" title="로그가 없어요" sub="웹소켓을 연결하고 메시지를 전송해보세요." size="sm" />
      <div v-else class="ws-test__logs">
        <pre v-for="(log, idx) in logs" :key="idx" class="ws-test__log">{{ log }}</pre>
      </div>
    </ACard>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "WebSocketPreviewTest",
  components: { APageHead, ACard, ABtn, APill, AEmpty },
  data() {
    return {
      socket: null,
      clientId: "section_test@example.com",
      barcode: "9781635190151",
      chapterNumber: 1,
      uniqueKey: 1,
      isConnected: false,
      logs: [],
    };
  },
  computed: {
    websocketUrl() {
      return `${this.$CONF.API_URL.replace(/^http/, "ws")}/textbook/ws/${this.clientId}`;
    },
    previewJson() {
      return JSON.stringify({
        barcode: this.barcode,
        chapterNumber: this.chapterNumber,
        uniqueKey: this.uniqueKey,
      }, null, 2);
    },
  },
  beforeUnmount() {
    this.disconnectWebSocket();
  },
  methods: {
    connectWebSocket() {
      if (this.isConnected) this.disconnectWebSocket();
      try {
        this.socket = new WebSocket(this.websocketUrl);
        this.socket.onopen = () => {
          this.isConnected = true;
          this.log(`✅ 연결됨: ${this.websocketUrl}`);
        };
        this.socket.onmessage = (e) => this.log(`📥 수신: ${e.data}`);
        this.socket.onerror = (e) => this.log(`❌ 에러: ${e.message || 'Unknown Error'}`);
        this.socket.onclose = (e) => {
          this.isConnected = false;
          this.log(`🔌 연결 종료 (코드: ${e.code})`);
        };
      } catch (error) {
        this.log(`❗ 연결 실패: ${error.message}`);
      }
    },
    disconnectWebSocket() {
      if (this.socket) this.socket.close(1000, "연결 종료 요청");
    },
    sendPreview() {
      if (!this.socket || !this.isConnected) {
        this.log("⚠️ 웹소켓이 연결되어 있지 않습니다.");
        return;
      }
      try {
        const msg = {
          type: "preview_request",
          barcode: this.barcode,
          chapterNumber: this.chapterNumber,
          uniqueKey: this.uniqueKey,
        };
        this.socket.send(JSON.stringify(msg));
        this.log(`📤 전송: ${JSON.stringify(msg)}`);
      } catch (err) {
        this.log(`❌ 전송 실패: ${err.message}`);
      }
    },
    clearLogs() { this.logs = []; },
    log(message) {
      const now = new Date().toLocaleTimeString();
      let formatted = message;
      try {
        if (typeof message === "object") formatted = JSON.stringify(message, null, 2);
        else if (typeof message === "string" && message.trim().startsWith("{")) {
          formatted = JSON.stringify(JSON.parse(message), null, 2);
        }
      } catch (e) {
        // noop
      }
      this.logs.unshift(`[${now}]\n${formatted}`);
      if (this.logs.length > 100) this.logs.pop();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ws-test {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .p-field:first-child,
    .p-field:nth-child(2) {
      grid-column: span 1;
    }

    .p-field:nth-child(n+3) {
      grid-column: span 2;
    }

    @media (min-width: 769px) {
      .p-field:nth-child(3),
      .p-field:nth-child(4),
      .p-field:nth-child(5) {
        grid-column: span 1;
      }
    }
  }

  &__preview {
    margin-top: 14px;
  }

  &__actions {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__log-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__log-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__logs {
    background: $p-sidebar;
    color: $p-sidebar-tx;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    max-height: 360px;
    overflow-y: auto;
    font-family: $p-font-mono;
    font-size: 11px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__log {
    margin: 0;
    line-height: 1.55;
    color: $p-sidebar-tx;
    white-space: pre-wrap;
    border-bottom: 1px solid $p-sidebar-line;
    padding-bottom: 8px;

    &:last-child { border-bottom: 0; padding-bottom: 0; }
  }
}
</style>
